import * as grpc from './grpc'

class ApiService {
  constructor() {
    this._jwt = null
  }

  setJWT(jwt) {
    this._jwt = jwt
  }

  _requireJWT() {
    if (!this._jwt) throw new Error('JWT not set - user must be authenticated')
    return this._jwt
  }

  // --- Health ---

  async check() {
    await grpc.check(this._requireJWT())
  }

  async clearCache() {
    await grpc.clearCache(this._requireJWT())
  }

  async checkValidity() {
    return grpc.checkValidity(this._requireJWT())
  }

  // --- Aggregated Data ---

  async getAllAggregatedData() {
    return grpc.getAllAggregatedData(this._requireJWT())
  }

  async getAggregatedDataForPaths(paths) {
    return grpc.getAggregatedDataForPaths(this._requireJWT(), paths)
  }

  async getActiveUnitTypePaths() {
    return grpc.getActiveUnitTypePaths(this._requireJWT())
  }

  // --- Site / Unit Data ---

  async getSiteNames() {
    return grpc.getSiteNames(this._requireJWT())
  }

  async getSiteWhitelist() {
    return grpc.getSiteWhitelist(this._requireJWT())
  }

  async getUnitTypeAreas() {
    return grpc.getUnitTypeAreas(this._requireJWT())
  }

  async getSiteGeo(path = '') {
    return grpc.getSiteGeo(this._requireJWT(), path)
  }

  // --- Timeseries ---

  async getTimeseries(path, dataKey) {
    return grpc.getTimeseries(this._requireJWT(), path, dataKey)
  }

  async getManyTimeseries(pathKeyMap, windowDays = 365) {
    return grpc.getManyTimeseries(this._requireJWT(), pathKeyMap, windowDays)
  }

  async getRentalPriceHistory(unitPk, rentalId) {
    return grpc.getRentalPriceHistory(this._requireJWT(), unitPk, rentalId)
  }

  // --- WTF ---

  async getWtf() {
    return grpc.getWtf(this._requireJWT())
  }

  async getWtfInputs() {
    return grpc.getWtfInputs(this._requireJWT())
  }

  async getWtfConfigurations() {
    return grpc.getWtfConfigurations(this._requireJWT())
  }

  async createWtfConfiguration(userId, configId, configJson) {
    return grpc.createWtfConfiguration(this._requireJWT(), userId, configId, configJson)
  }

  async updateWtfConfiguration(userId, configId, configJson, activate) {
    return grpc.updateWtfConfiguration(this._requireJWT(), userId, configId, configJson, activate)
  }

  async archiveWtfConfiguration(configId) {
    return grpc.archiveWtfConfiguration(this._requireJWT(), configId)
  }

  async activateWtfConfiguration(configId) {
    return grpc.activateWtfConfiguration(this._requireJWT(), configId)
  }

  async evaluateWtfConfiguration(configJson) {
    return grpc.evaluateWtfConfiguration(this._requireJWT(), configJson)
  }

  async executeWtfConfiguration(configId, saveData = true) {
    return grpc.executeWtfConfiguration(this._requireJWT(), configId, saveData)
  }

  async validateWtfConfiguration(configJson) {
    return grpc.validateWtfConfiguration(this._requireJWT(), configJson)
  }

  async validateWtfCompoundInput(expression) {
    return grpc.validateWtfCompoundInput(this._requireJWT(), expression)
  }

  // --- Price Changes ---

  async getPriceChanges() {
    return grpc.getPriceChanges(this._requireJWT())
  }

  watchPriceChanges(user, signal) {
    return grpc.watchPriceChanges(this._requireJWT(), user, signal)
  }

  async updatePriceChanges(changes, user) {
    return grpc.updatePriceChanges(this._requireJWT(), changes, user)
  }

  // --- Flags ---

  async getFlags(path) {
    return grpc.getFlags(this._requireJWT(), path)
  }

  async getManyFlags(paths) {
    return grpc.getManyFlags(this._requireJWT(), paths)
  }

  // --- Pipeline ---

  async getPipelineStatus() {
    return grpc.getPipelineStatus(this._requireJWT())
  }

  async forceDeployToStorman() {
    return grpc.forceDeployToStorman(this._requireJWT())
  }

  // --- Rentals ---

  async getUnitRentals(path) {
    return grpc.getUnitRentals(this._requireJWT(), path)
  }

  async getAllUnitRentalPriceChanges() {
    return grpc.getAllUnitRentalPriceChanges(this._requireJWT())
  }

  async updateRentalPriceChanges(updates, deployedBy) {
    return grpc.updateRentalPriceChanges(this._requireJWT(), updates, deployedBy)
  }

  async getRentalPriceHistory(unitPk, rentalId) {
    return grpc.getRentalPriceHistory(this._requireJWT(), unitPk, rentalId)
  }

  // --- ECR Config ---

  async getGlobalEcrConfig() {
    return grpc.getGlobalEcrConfig(this._requireJWT())
  }

  async updateGlobalEcrConfig(configEntries) {
    return grpc.updateGlobalEcrConfig(this._requireJWT(), configEntries)
  }

  async getExclusionEcrConfig() {
    return grpc.getExclusionEcrConfig(this._requireJWT())
  }

  async updateExclusionEcrConfig(configEntries) {
    return grpc.updateExclusionEcrConfig(this._requireJWT(), configEntries)
  }

  async getTierBoundEcrConfig() {
    return grpc.getTierBoundEcrConfig(this._requireJWT())
  }

  async updateTierBoundEcrConfig(configEntries) {
    return grpc.updateTierBoundEcrConfig(this._requireJWT(), configEntries)
  }

  // --- Competitor Weights ---

  async getCompetitorWeights(siteId) {
    return grpc.getCompetitorWeights(this._requireJWT(), siteId)
  }

  async updateCompetitorWeights(updates) {
    return grpc.updateCompetitorWeights(this._requireJWT(), updates)
  }

  // --- User Management ---

  async listUsers() {
    return grpc.listUsers(this._requireJWT())
  }

  async createUser({ firstName, lastName, email, role }) {
    return grpc.createUser(this._requireJWT(), firstName, lastName, email, role)
  }

  async disableUser(userId) {
    return grpc.disableUser(this._requireJWT(), userId)
  }

  async enableUser(userId) {
    return grpc.enableUser(this._requireJWT(), userId)
  }

  async forcePasswordReset(userId) {
    return grpc.forcePasswordReset(this._requireJWT(), userId)
  }

  async changeUserRole(userId, newRole) {
    return grpc.changeUserRole(this._requireJWT(), userId, newRole)
  }

  async resetUserMFA(userId) {
    return grpc.resetUserMFA(this._requireJWT(), userId)
  }

  // --- Version ---

  async getBackendVersion() {
    const info = await grpc.getVersionInfo(this._requireJWT())
    return info.version || '0.0.0'
  }

  async getVersionInfo() {
    return grpc.getVersionInfo(this._requireJWT())
  }
}

const apiService = new ApiService()
export default apiService
